import type { AppState } from "./types";
import type { Dialogs } from "./dialogs";
import { e, formatTime } from "./dom";
import { home, loader } from "./views/home";
import { roomView } from "./views/room";
import { matchView } from "./views/match";
import { localPlayer, viewedPlayer } from "./views/shared";
import { collapseWarningCopy } from "./collapse-warning";
import { publicOnlineCount } from "./presence";

/** Rebuild only structural changes; patch the ticking HUD without restarting UI animation. */
export function createRenderer(
  state: AppState,
  dialogs: Dialogs,
  onViewChanged: () => void,
): (force?: boolean) => void {
  const app = document.querySelector<HTMLElement>("#app")!;
  let lastViewKey = "";
  function viewKey(): string {
    const s = state.snapshot,
      me = localPlayer(state),
      seen = viewedPlayer(state);
    if (state.screen === "home")
      return `home:${state.name}:${state.playerId}:${state.connected}:${state.engineError}`;
    if (state.screen === "loader") return "loader";
    if (state.screen === "room")
      return `room:${JSON.stringify(state.room)}:${state.engineReady}`;
    if (!state.engineReady) return "match:loading-arena";
    return JSON.stringify([
      "match",
      s?.phase,
      s?.round,
      state.room?.kind,
      s?.winners,
      s?.phase === "match_over" ? s?.players : null,
      seen?.id,
      seen?.name,
      state.name,
      seen?.loadout,
      seen?.upgrade_ranks,
      me?.alive,
      s?.phase === "shop"
        ? [
            me?.gold,
            me?.ready,
            me?.loadout,
            me?.loadout_revision,
            me?.upgrade_ranks,
            state.pendingRequest,
            state.pendingReorder,
            state.selectedSlot,
            state.connected,
          ]
        : null,
      s?.team_scores,
    ]);
  }
  function render(force = false): void {
    const key = viewKey();
    if (force || key !== lastViewKey) {
      const focused =
        document.activeElement instanceof HTMLElement &&
        app.contains(document.activeElement)
          ? document.activeElement.closest<HTMLElement>("[data-spell-slot]")
          : null;
      const focusedSlot = focused?.dataset.spellSlot;
      const focusedPlayer = focused?.closest<HTMLElement>("[data-spell-reorder]")
        ?.dataset.playerId;
      const shopScroll = app.querySelector(".shop-body")?.scrollTop;
      lastViewKey = key;
      app.innerHTML =
        state.screen === "home"
          ? home(state)
          : state.screen === "loader"
            ? loader()
            : state.screen === "room" && state.room
              ? roomView(state)
              : !state.engineReady
                ? '<main class="loader-screen arena-loading" data-arena-loading aria-label="Loading arena"><span class="loader" role="status"><span class="sr-only">Loading the arena…</span></span></main>'
                : state.snapshot
                  ? matchView(state)
                  : loader();
      app.inert = !!dialogs.kind;
      document.body.dataset.screen = state.screen;
      onViewChanged();
      const shopBody = app.querySelector(".shop-body");
      if (shopBody && shopScroll != null) shopBody.scrollTop = shopScroll;
      if (focusedSlot != null) {
        const replacement = [
          ...app.querySelectorAll<HTMLElement>("[data-spell-slot]"),
        ].find(
          (slot) =>
            slot.dataset.spellSlot === focusedSlot &&
            slot.closest<HTMLElement>("[data-spell-reorder]")?.dataset.playerId ===
              focusedPlayer,
        );
        replacement?.focus({ preventScroll: true });
      }
    }
    updateLiveUI();
    dialogs.refresh(state);
  }
  function setText(selector: string, value: string): void {
    const node = app.querySelector(selector);
    if (node && node.textContent !== value) node.textContent = value;
  }
  function updateLiveUI(): void {
    setText(
      "[data-presence]",
      state.online == null
        ? state.connected
          ? "Online count unavailable"
          : "Connecting"
        : `${publicOnlineCount(state.online)} players online`,
    );
    const s = state.snapshot;
    if (!s || state.screen !== "match") return;
    setText("[data-alive]", String(s.players.filter((p) => p.alive).length));
    app.querySelectorAll<HTMLElement>("[data-team-alive]").forEach((node) => {
      const team = Number(node.dataset.teamAlive);
      node.textContent = `${s.players.filter((p) => p.team === team && p.alive).length} alive`;
    });
    setText("[data-round-time]", formatTime(s.round_tick / s.tick_rate));
    setText(
      "[data-phase-time]",
      s.phase === "shop"
        ? formatTime(s.phase_ticks_remaining / s.tick_rate)
        : String(Math.ceil(s.phase_ticks_remaining / s.tick_rate)),
    );
    setText(
      "[data-ready-count]",
      `${s.players.filter((p) => p.ready).length} / ${s.players.length} ready`,
    );
    const me = localPlayer(state),
      viewed = viewedPlayer(state);
    if (me) setText("[data-wallet]", String(me.gold));
    app
      .querySelector("[data-collapse]")
      ?.classList.toggle(
        "hidden",
        s.collapse_warning == null || s.phase !== "combat",
      );
    setText("[data-collapse-message]", collapseWarningCopy(s));
    app.querySelectorAll<HTMLElement>("[data-cooldown]").forEach((node) => {
      const i = Number(node.dataset.cooldown),
        ticks = viewed?.cooldowns[i] ?? 0;
      node.classList.toggle("active", ticks > 0);
      const seconds = ticks / s.tick_rate;
      const next = seconds > 0 ? (Math.ceil(seconds * 10) / 10).toFixed(1) : "";
      if (node.textContent !== next) node.textContent = next;
    });
    const feed = app.querySelector("[data-feed]");
    if (feed) {
      const items = s.events
        .filter(
          (ev) =>
            ev.kind.type === "eliminated" && s.tick - ev.tick < s.tick_rate * 6,
        )
        .slice(-3);
      const html = items
        .map((ev) => {
          const victim = s.players.find((p) => p.id === ev.kind.player),
            killer = s.players.find((p) => p.id === ev.kind.credited_to);
          const cause =
            ev.kind.cause === "spell"
              ? killer
                ? `was defeated by <strong>${e(killer.name)}</strong>`
                : "was defeated by a spell"
              : killer
                ? `burned in the lava · <strong>${e(killer.name)}</strong> gets the credit`
                : "burned in the lava";
          return `<div><strong>${e(victim?.name ?? "A wizard")}</strong> ${cause}</div>`;
        })
        .join("");
      if (feed.innerHTML !== html) feed.innerHTML = html;
    }
  }

  return render;
}
